/**
 * Crypto Service
 * 
 * Provides encryption/decryption functionality for storage services.
 * Keys are derived dynamically from the user's auth token — no static secrets needed.
 * 
 * Flow:
 * 1. User logs in → token received from backend
 * 2. cryptoService.initializeWithToken(token) is called
 * 3. Encryption/key-hashing keys are derived from the token via SHA-256
 * 4. All storage reads/writes use the derived keys
 * 5. On page reload: raw token is read from sessionStorage → keys re-derived
 * 6. On logout: keys are cleared, encrypted storage becomes unreadable
 */

// The raw token key is stored unencrypted under this fixed name
const RAW_TOKEN_STORAGE_KEY = '_auth_token';

class CryptoService {
    constructor() {
        this.keyLength = 32; // 256 bits
        this.enabled = false;
        this.key = null;
        this._keyHashSalt = null;
    }

    /**
     * Initialize encryption using the user's auth token.
     * Must be called after login and on page reload (before any encrypted storage access).
     * @param {string} token - The JWT or access token from the backend
     */
    initializeWithToken(token) {
        if (!token || typeof token !== 'string' || token.length < 10) {
            this.enabled = false;
            this.key = null;
            this._keyHashSalt = null;
            return;
        }

        const encoder = new TextEncoder();

        // Derive the encryption key from token + a purpose-specific salt
        const encKeyData = encoder.encode(token + '::storage-encryption-key');
        this.key = this._simpleHash(encKeyData);

        // Derive a separate key-hash salt from token + a different purpose salt
        const hashSaltData = encoder.encode(token + '::storage-key-hash-salt');
        this._keyHashSalt = this._simpleHash(hashSaltData);

        this.enabled = true;
    }

    /**
     * Try to bootstrap from a raw token already in sessionStorage (for page reloads).
     * Returns true if a token was found and keys were derived.
     * @returns {boolean}
     */
    tryBootstrapFromStorage() {
        try {
            if (typeof window === 'undefined' || !window.sessionStorage) return false;
            const rawToken = window.sessionStorage.getItem(RAW_TOKEN_STORAGE_KEY);
            if (rawToken) {
                this.initializeWithToken(rawToken);
                return true;
            }
        } catch (e) {
            // sessionStorage not available
        }
        return false;
    }

    /**
     * Store the raw token unencrypted so it can bootstrap crypto on page reload.
     * @param {string} token
     */
    storeRawToken(token) {
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                window.sessionStorage.setItem(RAW_TOKEN_STORAGE_KEY, token);
            }
        } catch (e) {
            // sessionStorage not available
        }
    }

    /**
     * Clear the raw token and all derived keys (called on logout).
     */
    clearKeys() {
        this.enabled = false;
        this.key = null;
        this._keyHashSalt = null;
        try {
            if (typeof window !== 'undefined' && window.sessionStorage) {
                window.sessionStorage.removeItem(RAW_TOKEN_STORAGE_KEY);
            }
        } catch (e) {
            // ignore
        }
    }

    /**
     * Simple hash function for key derivation
     * @param {Uint8Array} data - Data to hash
     * @returns {Uint8Array} 32-byte hash result
     */
    _simpleHash(data) {
        let hash = new Uint8Array(32);
        for (let i = 0; i < data.length; i++) {
            hash[i % 32] ^= data[i];
        }
        
        // Add mixing rounds
        for (let round = 0; round < 10; round++) {
            for (let i = 0; i < 32; i++) {
                hash[i] = (hash[i] + hash[(i + 1) % 32] + round) % 256;
            }
        }
        
        return hash;
    }

    /**
     * Check if encryption is enabled (i.e., keys have been derived from a token).
     * @returns {boolean}
     */
    isEnabled() {
        return this.enabled && this.key !== null;
    }

    /**
     * XOR encrypt
     * @param {string} text - Plaintext
     * @returns {string} Base64-encoded ciphertext
     */
    _simpleXOR(text) {
        if (!this.key) return text;
        
        const result = [];
        for (let i = 0; i < text.length; i++) {
            result.push(String.fromCharCode(
                text.charCodeAt(i) ^ this.key[i % this.key.length]
            ));
        }
        return btoa(result.join(''));
    }

    /**
     * XOR decrypt
     * @param {string} encryptedText - Base64-encoded ciphertext
     * @returns {string} Plaintext
     */
    _simpleXORDecrypt(encryptedText) {
        if (!this.key) return encryptedText;
        
        try {
            const decoded = atob(encryptedText);
            const result = [];
            for (let i = 0; i < decoded.length; i++) {
                result.push(String.fromCharCode(
                    decoded.charCodeAt(i) ^ this.key[i % this.key.length]
                ));
            }
            return result.join('');
        } catch (error) {
            return encryptedText;
        }
    }

    /**
     * Encrypt data for storage
     * @param {string} plaintext
     * @returns {string} Encrypted data
     */
    encrypt(plaintext) {
        if (!this.isEnabled()) {
            throw new Error('CryptoService: Not initialized. Call initializeWithToken() first.');
        }
        return this._simpleXOR(plaintext);
    }

    /**
     * Decrypt data from storage
     * @param {string} encryptedData
     * @returns {string} Decrypted data
     */
    decrypt(encryptedData) {
        if (!this.isEnabled()) {
            throw new Error('CryptoService: Not initialized. Call initializeWithToken() first.');
        }

        try {
            if (!this._isBase64(encryptedData)) {
                // Legacy unencrypted data — return as-is
                return encryptedData;
            }
            return this._simpleXORDecrypt(encryptedData);
        } catch (error) {
            return encryptedData;
        }
    }

    /**
     * Check if a string is valid base64
     * @param {string} str
     * @returns {boolean}
     */
    _isBase64(str) {
        try {
            return btoa(atob(str)) === str;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get crypto service status
     * @returns {Object}
     */
    getStatus() {
        return {
            enabled: this.enabled,
            algorithm: 'XOR (Browser Compatible)',
            hasKey: this.key !== null,
            keyLength: this.key ? this.key.length : 0
        };
    }

    /**
     * Hash a storage key to make it unreadable in DevTools
     * @param {string} originalKey
     * @returns {string} Hashed key
     */
    hashStorageKey(originalKey) {
        if (!this.isEnabled() || !this._keyHashSalt) {
            throw new Error('CryptoService: Not initialized. Call initializeWithToken() first.');
        }

        const encoder = new TextEncoder();
        const combinedData = originalKey + String.fromCharCode.apply(null, this._keyHashSalt) + 'key-salt-2025';
        const data = encoder.encode(combinedData);
        
        const hash = this._simpleHash(data);
        const hashArray = Array.from(hash);
        
        return btoa(String.fromCharCode.apply(null, hashArray)).replace(/[+/=]/g, '');
    }

    /**
     * Check if key hashing is available
     * @returns {boolean}
     */
    canHashKeys() {
        return this.isEnabled() && this._keyHashSalt !== null;
    }
}

// Singleton instance
const cryptoService = new CryptoService();

// On module load, try to bootstrap from an existing raw token in sessionStorage (handles page reload)
cryptoService.tryBootstrapFromStorage();

export { CryptoService, RAW_TOKEN_STORAGE_KEY };
export default cryptoService;
